import { formatClockText, formatClockTime, getTimeFormat } from '@/shared/lib/time-format';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Search, ChevronLeft, ChevronRight, ChevronDown, ArrowRight, X } from 'lucide-react';
import {
  useProviderSlots,
  type ProviderSlotsResponse,
} from '@/services/scheduling/use-provider-slots';
import {
  usePreBookAppointmentProfile,
  pickDefaultReminder,
  pickAttachedBillingProfile,
  reminderRules,
  formatBillingProfile,
} from '@/services/scheduling/use-prebook-appointment-profile';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { NO_SLOT_AVAILABILITY_MESSAGE } from '@/scheduling/lib/appointment-strings';
import { filterProvidersForCurrentUser } from '@/scheduling/lib/provider-self-filter';
import {
  disciplineSpecialityIds,
  filterProvidersByDiscipline,
  noProviderForFilterText,
  warnIfPractitionerListMayBeCut,
} from '@/scheduling/lib/provider-discipline-filter';
import type { ProviderFilter } from '@/shared/store/create-appointment-handoff';
import { useAuthStore } from '@/auth/auth-store';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { useDoctorsList } from '@/settings/services/use-doctors-list';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { AppointmentSlot } from '@/types/scheduling-api';
import { formatSlotDate } from './slot-date';

interface ProviderSelectionViewProps {
  /** Closes the view without changes. */
  onCancel: () => void;
  /**
   * Opens the Time & Date view. Mirrors Flutter
   * `calendar_screen.dart:676–680` — `onMore` sets the provider + first
   * profile, then pushes the dateTimeSelection step.
   */
  onOpenTimeDate: () => void;
  /** Referral handoffs only: list one discipline. */
  providerFilter?: ProviderFilter;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStripDays(anchor: Date) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = startOfDay(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

/** Slot time in the current format ("13:45", or "1:45p" under the 12-hour preference). */
function fmt12h(iso: string) {
  return formatClockTime(new Date(iso));
}

const fmtSlotDate = formatSlotDate;

function getInitials(name: string) {
  const parts = name.replace(/^Dr\.?\s*/i, '').split(' ').filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

function formatWorkingHours(hours: { startTime?: string; endTime?: string }[] | undefined): string {
  if (!hours || hours.length === 0) return 'Non working';
  // Server sends "09:00 AM": 12-hour keeps the compact "09:00a", 24-hour reads "09:00".
  const compact = (t?: string) =>
    !t ? '' : getTimeFormat() === '12h' ? t.toLowerCase().slice(0, -1).replace(/\s+/g, '') : formatClockText(t);
  const start = compact(hours[0].startTime);
  const end = compact(hours[0].endTime);
  if (!start || !end) return 'Non working';
  return `${start} - ${end}`;
}

/**
 * ProviderSelectionView — full-content replacement of the create-appointment
 * panel. Mirrors Flutter `provider_selection_widget.dart`:
 *
 *   - First / Follow Up tab → drives `appointmentProfileType` in the request
 *   - Autofocus search input (server-side, 600ms debounce, min 2 chars)
 *   - 5-day rolling date strip + tappable date picker header
 *   - Provider list with avatar / name / specialty / hours and 2 inline
 *     slot chips (date + time + location) + a forward arrow for "more"
 *   - Slot tap → picks provider + first profile + slot, returns to create
 *   - Forward arrow → picks provider + first profile only, returns to create
 *
 * Backed by POST /api/Doctor/ProviderSlots with `slotCount: 2`,
 * `searchSlot: true`, and `appointmentProfileType` set.
 */
export function ProviderSelectionView({ onCancel, onOpenTimeDate, providerFilter }: ProviderSelectionViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const formDate = watch('selectedDate');
  const location = watch('location');

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';

  // Local state — Flutter's _ProviderSelectionWidgetState. Tab labels are
  // "First" / "Follow Up" but the wire enum (EnumAppointmentProfileType)
  // takes ROUTINE / FOLLOWUP — Flutter app_constants.dart:691.
  const [tab, setTab] = useState<'ROUTINE' | 'FOLLOWUP'>('ROUTINE');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 600);
  const effectiveSearch =
    debouncedSearch.length === 0 || debouncedSearch.length >= 2 ? debouncedSearch : null;

  const today = startOfDay(new Date());
  const initialDate = formDate ? startOfDay(new Date(formDate)) : today;
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Anchor at today so the strip layout matches the create-panel's
  // DatePickerSection. If the picked date falls outside [anchor, anchor+4],
  // the effect below shifts to keep the highlight visible.
  const [anchorDate, setAnchorDate] = useState<Date>(today);

  // Keep local selectedDate aligned with the form when the form value
  // changes — the create-panel writes `selectedDate` whenever the user
  // taps a date tile or uses the date dropdown, and we need that to
  // flow into the search view's strip + query.
  useEffect(() => {
    if (!formDate) return;
    const fd = startOfDay(new Date(formDate));
    setSelectedDate((prev) => (isSameDay(prev, fd) ? prev : fd));
  }, [formDate]);

  useEffect(() => {
    setAnchorDate((prev) => {
      const prevEnd = new Date(prev);
      prevEnd.setDate(prev.getDate() + 4);
      const startsBefore = selectedDate.getTime() < prev.getTime();
      const endsAfter = selectedDate.getTime() > prevEnd.getTime();
      return startsBefore || endsAfter ? selectedDate : prev;
    });
  }, [selectedDate]);

  const stripDays = getStripDays(anchorDate);
  // Today is the floor — past dates aren't bookable.
  const minDate = startOfDay(new Date());

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const locationId = location?.id;
  // Provider comes before location in the flow, so no location yet means the
  // practice's own locations (the list LocationSelector offers) — never `[]`,
  // which asks for every location in the organisation. No request while that
  // list is loading or empty.
  const { data: practiceLocations, isLoading: practiceLocationsLoading } = useOrgLocations(orgId || null);
  const practiceLocationKey = (practiceLocations ?? []).map((l) => l.id).join(',');
  const queryParams = useMemo(() => {
    if (!practiceId || !orgId || effectiveSearch === null) return null;
    const locationIds = locationId
      ? [locationId]
      : practiceLocationKey.split(',').filter(Boolean);
    if (locationIds.length === 0) return null;
    // No `as const` — the hook's param interface takes mutable
    // string[]/string values; the readonly literal shape narrows the
    // tuple types and breaks structural compat.
    return {
      organizationId: orgId,
      practiceId,
      date: selectedDate.toISOString(),
      locationIds,
      searchText: effectiveSearch,
      slotCount: 2,
      searchSlot: true,
      appointmentProfileType: tab,
    };
  }, [orgId, practiceId, selectedDate, locationId, practiceLocationKey, effectiveSearch, tab]);

  const { data: allProviders = [], isLoading: slotsLoading } = useProviderSlots(queryParams);
  // Waiting on the practice's locations reads as loading, not as "no providers".
  const isLoading = slotsLoading || (!locationId && practiceLocationsLoading);

  // PROD-1024 (F-SCH-132): self-filter the provider list for a doctor/nurse —
  // they book only themselves. Flutter create_appointment_widget.dart:2202-2210.
  const selfFilterUser = useAuthStore((s) => s.user);
  // Referral filter: the org practitioner list adds practitioners whose second speciality is the role (PROD-1395).
  const doctorsQuery = useDoctorsList(providerFilter ? orgId : null, false);
  const doctorRows = doctorsQuery.data?.length ?? 0;
  useEffect(() => warnIfPractitionerListMayBeCut(doctorRows), [doctorRows]);
  const disciplineSpecialityIdSet = useMemo(
    () => (providerFilter ? disciplineSpecialityIds(doctorsQuery.data, providerFilter.discipline) : undefined),
    [doctorsQuery.data, providerFilter],
  );
  const providers = useMemo(
    () =>
      filterProvidersByDiscipline(filterProvidersForCurrentUser(allProviders, selfFilterUser), providerFilter, disciplineSpecialityIdSet),
    [allProviders, selfFilterUser, providerFilter, disciplineSpecialityIdSet],
  );

  const preBook = usePreBookAppointmentProfile();

  // Auto-pick the first profile and PreBook it; matches Flutter
  // `calendar_screen.dart:643` (fetchPreSelectedApptProfileDetails after
  // both onSelection and onMore set the profile).
  const applyProviderAndProfile = (provider: ProviderSlotsResponse) => {
    setValue('providerId', provider.id, { shouldValidate: true });
    setValue('providerName', provider.name);
    setValue('providerSpeciality', specialityDisplay(provider.speciality) ?? null);
    const sCode = provider.speciality?.code as
      | { code?: string; display?: string; displayName?: string }
      | undefined;
    setValue(
      'providerSpecialityCode',
      sCode?.code
        ? {
            specialitySkillId: provider.specialityId,
            code: {
              code: sCode.code,
              displayName: sCode.displayName ?? sCode.display,
              system: 'https://snomed.info/sct',
              description: null,
              shortName: null,
            },
          }
        : null,
    );

    const firstProfile = provider.appointmentProfile?.[0];
    if (firstProfile) {
      setValue('appointmentProfileId', firstProfile.id ?? null, { shouldValidate: true });
      setValue('appointmentProfileName', firstProfile.profileName ?? null);
      setValue('appointmentProfileColor', firstProfile.color ?? null);
      // appointmentType (PascalCase tolerant) — same logic as
      // appointment-profile-section so the BookAppointment payload
      // doesn't ship `null` here.
      const at = firstProfile.appointmentType as
        | (typeof firstProfile.appointmentType & {
            Code?: string;
            Display?: string;
            Definition?: string;
          })
        | undefined;
      const atCode = at?.code ?? at?.Code;
      const atDisplay = at?.display ?? at?.Display;
      setValue(
        'appointmentType',
        atCode && atDisplay
          ? {
              code: atCode,
              display: atDisplay,
              definition: at?.definition ?? at?.Definition ?? '',
            }
          : null,
        { shouldValidate: true },
      );
      // reasonCode (PascalCase tolerant) — backend sometimes ships
      // `ReasonCode` capitalised, so reading `.reasonCode` returns
      // undefined and the schema's `reasonCode.code required` rule trips
      // with no obvious cause.
      const rc = firstProfile.reasonCode as
        | (typeof firstProfile.reasonCode & {
            Code?: string;
            Display?: string;
            Definition?: string;
          })
        | undefined;
      const rcCode = rc?.code ?? rc?.Code;
      const rcDisplay = rc?.display ?? rc?.Display;
      setValue(
        'reasonCode',
        rcCode && rcDisplay
          ? {
              code: rcCode,
              display: rcDisplay,
              definition: rc?.definition ?? rc?.Definition ?? '',
            }
          : null,
        // shouldValidate so the schema re-runs against the new value;
        // without this, validation may have run on an empty reasonCode
        // and left a stale "required" error.
        { shouldValidate: true },
      );
      setValue('minutesDuration', firstProfile.minutesDuration ?? null);
      setValue('pickedProfile', firstProfile);

      if (firstProfile.id && provider.id) {
        preBook.mutate(
          {
            organizationId: orgId,
            practiceId,
            providerId: provider.id,
            appointmentProfileId: firstProfile.id,
            date: selectedDate.toISOString(),
          },
          {
            onSuccess: (response) => {
              const reminder = pickDefaultReminder(response.reminderList);
              if (reminder?.id) {
                setValue('reminderProfileId', reminder.id, { shouldValidate: true });
                setValue('reminderProfileName', reminder.profileName ?? null);
                setValue('reminderProfileRules', reminderRules(reminder));
              }
              const billing = pickAttachedBillingProfile(firstProfile, response.billingProfiles);
              if (billing?.id) {
                setValue('billingProfileId', billing.id);
                setValue('billingProfileName', formatBillingProfile(billing));
              }
              if (response.providerReasonForVisits?.length) {
                setValue(
                  'reasonCodeOptions',
                  response.providerReasonForVisits.map((r) => ({
                    code: r.code,
                    display: r.display,
                    definition: r.definition,
                  })),
                );
              }
            },
          },
        );
      }
    }
  };

  // Selection — picks provider + first profile + slot.
  // Mirrors Flutter calendar_screen.dart:600–645 (onSelection).
  const onSelection = (provider: ProviderSlotsResponse, slot: AppointmentSlot) => {
    applyProviderAndProfile(provider);

    if (slot.start && slot.end) {
      setValue('slotId', `${provider.id}-${slot.start}`, { shouldValidate: true });
      setValue('slotStart', slot.start);
      setValue('slotEnd', slot.end);
      setValue('selectedDate', slot.start);
    }

    onCancel();
  };

  // Forward arrow — picks provider + first profile, then pushes the
  // Time & Date view (Flutter `calendar_screen.dart:646–681` —
  // CreateAppointmentStartEvent(dateTimeSelection)).
  const onMore = (provider: ProviderSlotsResponse) => {
    applyProviderAndProfile(provider);
    onOpenTimeDate();
  };

  const shiftStrip = (deltaDays: number) => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + deltaDays);
    if (d.getTime() < minDate.getTime()) return;
    setAnchorDate(d);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar — matches the main panel header: title + X close */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 text-[length:var(--type-body-size)] font-medium text-text-secondary hover:text-text-primary"
        >
          <ChevronLeft size={16} strokeWidth={1.5} />
          Back
        </button>
        <h2 className="text-[length:var(--type-section-size)] font-semibold leading-[var(--type-section-line)] text-text-primary">Select a provider</h2>
        <button type="button" onClick={onCancel} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-md text-text-secondary hover:bg-grey-100 hover:text-text-primary">
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-4">
          {/* First / Follow Up tabs */}
          <div className="flex p-1 rounded-lg bg-grey-100 mx-auto w-fit">
            {([
              { value: 'ROUTINE',  label: 'First' },
              { value: 'FOLLOWUP', label: 'Follow Up' },
            ] as const).map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={cn(
                  'px-6 py-1.5 text-sm font-medium rounded-md transition-colors',
                  tab === t.value
                    ? 'bg-surface-card text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-primary',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
            <input
              ref={inputRef}
              placeholder="Search specialties or providers"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              maxLength={100}
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-border-default bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:border-brand-primary focus:bg-surface-card focus:outline-none"
            />
          </div>

          {/* Date picker header + strip */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <button type="button" onClick={() => shiftStrip(-5)} className="p-1 hover:bg-grey-100 rounded" aria-label="Previous days">
                <ChevronLeft className="h-4 w-4 text-text-muted" />
              </button>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger
                  render={
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-text-primary hover:text-brand-primary"
                    >
                      <span>
                        {MONTH_LONG[selectedDate.getMonth()]} {String(selectedDate.getDate()).padStart(2, '0')}, {selectedDate.getFullYear()}
                      </span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  }
                />
                <PopoverContent align="center" className="w-auto p-2">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    defaultMonth={selectedDate}
                    onSelect={(d) => {
                      if (d) {
                        setSelectedDate(startOfDay(d));
                        setPickerOpen(false);
                      }
                    }}
                    disabled={{ before: minDate }}
                    showOutsideDays
                    fixedWeeks
                  />
                  {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value. */}
                  <div className="mt-2 flex items-center justify-between border-t border-border-default pt-2 text-size-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDate(startOfDay(new Date()));
                        setPickerOpen(false);
                      }}
                      className="font-medium text-brand-primary hover:underline"
                    >
                      Today
                    </button>
                    <span className="text-text-muted">
                      {MONTH_LONG[selectedDate.getMonth()]} {String(selectedDate.getDate()).padStart(2, '0')}, {selectedDate.getFullYear()}
                    </span>
                  </div>
                </PopoverContent>
              </Popover>
              <button type="button" onClick={() => shiftStrip(5)} className="p-1 hover:bg-grey-100 rounded" aria-label="Next days">
                <ChevronRight className="h-4 w-4 text-text-muted" />
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1">
              {stripDays.map((d) => {
                const isSelected = isSameDay(d, selectedDate);
                const isPast = d.getTime() < minDate.getTime();
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    disabled={isPast}
                    onClick={() => setSelectedDate(d)}
                    className={cn(
                      'flex flex-col items-center py-2 rounded-xl border transition-colors',
                      isSelected
                        ? 'bg-brand-primary border-brand-primary text-white'
                        : isPast
                          ? 'border-border-default text-text-hint cursor-not-allowed opacity-50'
                          : 'border-border-default text-text-muted hover:border-brand-primary hover:text-text-primary',
                    )}
                  >
                    <span className="text-xs font-medium">{MONTH_SHORT[d.getMonth()]}</span>
                    <span className="text-md font-bold leading-tight">{d.getDate()}</span>
                    <span className="text-xs font-medium">{DAY_NAMES[d.getDay()]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider list */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : providers.length === 0 ? (
            <p className="text-center text-sm text-text-muted py-8">
              {providerFilter && effectiveSearch === '' ? noProviderForFilterText(providerFilter) : 'Provider not found'}
            </p>
          ) : (
            <div className="space-y-4">
              {providers.map((p, i) => (
                <ProviderRow
                  key={p.id || `provider-${i}`}
                  provider={p}
                  locationName={location?.name ?? ''}
                  onSlotClick={(slot) => onSelection(p, slot)}
                  onMore={() => onMore(p)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ProviderRowProps {
  provider: ProviderSlotsResponse;
  locationName: string;
  onSlotClick: (slot: AppointmentSlot) => void;
  onMore: () => void;
}

function ProviderRow({ provider, locationName, onSlotClick, onMore }: ProviderRowProps) {
  const slots = (provider.slots ?? []).slice(0, 2);
  const hasSlots = slots.length > 0;

  return (
    // Visual-only frame matching the inner slot card shape (rounded-xl
    // border-border-default). Not clickable itself — selection still
    // happens via the slot buttons / arrow inside.
    <div className="rounded-xl border border-border-default bg-surface-card p-3">
      <div className="flex items-start gap-2 mb-2">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={provider.photo ?? undefined} />
          <AvatarFallback className="text-size-2xs font-semibold bg-blue-100 text-brand-depth">
            {getInitials(provider.name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">
            {provider.name.replace(/^Dr\.?\s*/i, 'Dr. ')}
          </p>
          <p className="text-xs text-text-muted truncate">
            {specialityDisplay(provider.speciality) ?? 'N/A'}
          </p>
          <p className="text-xs text-text-muted truncate">
            {formatWorkingHours(provider.workingHours)}
          </p>
        </div>
      </div>

      {hasSlots ? (
        <div className="flex items-center gap-2 pl-10">
          <div className="flex-1 grid grid-cols-2 gap-2">
            {slots.map((slot, i) => (
              <button
                key={`${slot.start}-${i}`}
                type="button"
                onClick={() => onSlotClick(slot)}
                className="flex flex-col items-center gap-1 px-2 py-2 rounded-xl border border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100 transition-colors"
              >
                <span className="text-xs font-medium text-text-muted">
                  {fmtSlotDate(slot.start ?? '')}
                </span>
                <span className="text-base font-bold text-text-primary leading-tight">
                  {fmt12h(slot.start ?? '')}
                </span>
                <span className="text-xs text-brand-primary leading-tight">
                  {locationName}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onMore}
            className="h-8 w-8 flex items-center justify-center rounded-full border border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100 transition-colors flex-shrink-0"
            aria-label="More slots"
          >
            <ArrowRight className="h-4 w-4 text-brand-primary" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pl-10">
          <p className="flex-1 text-sm text-text-muted">
            {NO_SLOT_AVAILABILITY_MESSAGE}
          </p>
          <button
            type="button"
            onClick={onMore}
            className="h-8 w-8 flex items-center justify-center rounded-full border border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100 transition-colors flex-shrink-0"
            aria-label="More slots"
          >
            <ArrowRight className="h-4 w-4 text-brand-primary" />
          </button>
        </div>
      )}
    </div>
  );
}
